/**
 * Миксин предназначен для загрузки и работы с группой элементов, зачастую его нужно использовать с группой у которой есть пагинация
 */

import baseDataMixin from '~/mixins/baseDataMixin'

export default {
  mixins: [baseDataMixin],
  props: {
    /**
     * Первоначальные данные для компонента, если они прокинуты, то первоначальная загрузка не произойдет и в виде данных в success будет прокинут этот пропс
     */
    initialValue: {
      type: Object,
      default: null,
    },
    /**
     * Функция, которая будет вызвана при загрузке компонента в fetch'e, зачастую нужно передавать функцию из API-модуля
     */
    get: {
      type: Function,
    },
    /**
     * Объект с параметрами, объект напрямую прокидывается в функцию из пропса get
     */
    params: {
      type: Object,
      default: () => ({}),
    },
    /**
     * если поставить true, то при обновлении объекта в пропсе params будет происходить запрос с обновленными параметрами
     */
    watchParamChanges: {
      type: Boolean,
      default: false,
    },
    /**
     * Нужно понимать, что в ответе из API список элементов (товаров, акций, новостей) постоянно лежит в разном месте
     * и нужно подсказать компоненту где именно, чтобы он мог его взять и использовать. Эта функция вызывается сразу
     * после осуществления запроса и в параметры функции прокидывается результат запроса. Например в запросе каталога
     * список продуктов лежит по такому пути product, значит функцию нужно написать такую (res) => res.product
     */
    getList: {
      type: Function,
      default: (data) => {
        return data.product || []
      },
    },
    /**
     * Тоже самое что и getList, нужно подсказать компоненту где лежит сортировка, в случае того же каталога нужно передать (res) => res.sort
     */
    getSortList: {
      type: Function,
      default: (data) => {
        return data.sort || []
      },
    },
    /**
     * Тоже самое что и getList, нужно подсказать компоненту где лежит пагинация, в случае того же каталога нужно передать (res) => res.pagination
     */
    getPaginationData: {
      type: Function,
      default: (data) => {
        return data.pagination || {}
      },
    },
    requestMiddleware: {
      type: Function,
      default: config => config,
    },
  },
  async fetch() {
    if (this.initialValue) {
      this.getRequestParams().onSuccess(this.initialValue)
      this.changeStatus('success')
      this.$emit('success', { ...this.externalAPI, ...this._api })
    }
    else {
      await this.request(this.getRequestParams())
    }
  },
  data() {
    return {
      localParams: this.params,
      externalAPI: {
        response: null,
        list: [],
        itemsCount: null,
        initialLoad: true,
        pageCurrent: null,
        pageCount: null,
        sortList: [],
        activeSort: {},
      },
    }
  },
  watch: {
    params: {
      deep: true,
      handler(val) {
        this.localParams = { ...this.localParams, ...val }
      },
    },
    watchParamChanges(val) {
      if (val && !this.resetWatcher) {
        this.resetWatcher = this.$watch(
          'params',
          () => {
            this.request(this.getRequestParams())
          },
          {
            deep: true,
          },
        )
      }
      else if (this.resetWatcher) {
        this.resetWatcher()
      }
    },
  },
  created() {
    this.appendMethodToAPI({ name: 'changePage', fn: this.changePage })
    this.appendMethodToAPI({ name: 'loadMore', fn: this.loadMore })
    this.appendMethodToAPI({ name: 'sort', fn: this.sort })
    this.appendMethodToAPI({ name: 'resetPagination', fn: this.resetPagination })
    this.appendMethodToAPI({
      name: 'get',
      fn: () => {
        this.request(this.getRequestParams())
      },
    })

    if (this.watchParamChanges) {
      this.resetWatcher = this.$watch(
        'params',
        () => {
          this.request(this.getRequestParams())
        },
        {
          deep: true,
        },
      )
    }
  },
  beforeMount() {
    this.clientPipeline()
  },
  destroyed() {
    this.resetWatcher?.()
  },
  methods: {
    /**
     * При загрузке странице с нуля сначала на сервере и потом на клиенте, накст не видит изменения в data-свойствах, если эти изменения произошли через emit,
     * и поэтому он эти изменения не может передать на клиент. Поэтому нужно повторно вызывать нужный emit еще раз на клиенте, чтобы на клинте также отображалось валидное состояние
     */
    clientPipeline() {
      if (this.externalAPI.status === 'success')
        this.$emit('success', { ...this.externalAPI, ...this._api })
      else if (this.externalAPI.status === 'error')
        this.$emit('error', { ...this.externalAPI, ...this._api })
    },
    /**
     * Функция которая возвращает объект, который будет использоваться при запросе к api
     * @param {string} obj.strategy - метод поведения при загрузке дополнительных элементов, мы либо дополняем существующий список, либо перезаписываем его. Доступные значения replace и append
     * @returns {object} - объект, который будет использоваться в функции request
     */
    getRequestParams({ strategy = 'replace' } = {}) {
      return {
        requestStrategy: this.localParams.cancel ? 'last-request-priority' : 'first-request-priority',
        /**
         * Нужно ли сбрасывать первый запрос, если есть второй запрос
         * По умолчанию не сбрасываем
         */
        replaceCurrentLoading: this.replaceCurrentLoading(),
        api: () => {
          /**
           * getRequest по умолчанию вовзращает данные из пропса get
           * getLocalParams по умолчанию вовзращает данные из дата-свойства localParams
           * Мы создали эти методы, чтобы их можно легко кастомизировать в конкретных случаях в других умных компонентах.
           * Пример этой кастомизации можно посмотреть в компоненте DataEntityCatalogList
           */
          return this.getRequest()(this.requestMiddleware(this.getLocalParams()))
        },
        onSuccess: (data) => {
          if (strategy === 'replace') {
            /**
             * getDataRequest по умолчанию вовзращает данные из data
             * Мы создали этот метод, чтобы данные можно легко кастомизировать в конкретных случаях в других умных компонентах.
             * Пример этой кастомизации можно посмотреть в компоненте DataEntityCatalogList
             */
            this.externalAPI.response = this.getDataRequest(data)
            /**
             * getListRequest по умолчанию вовзращает данные из пропса getList
             * Этот метод создан также, чтобы его можно легко изменить в других умных компонентах.
             * Пример его изменения можно посмотреть в компоненте DataEntityCatalogList
             */
            this.externalAPI.list = this.getListRequest()(data) || []
          }
          else if (strategy === 'append') {
            this.externalAPI.response = this.getDataRequest(data)
            const list = this.getListRequest()(data) || []
            this.externalAPI.list.push(...list)
          }
          /**
           * getSortListRequest по умолчанию вовзращает данные из пропса getSortList
           * Этот метод создан также, чтобы его можно легко изменить в других умных компонентах.
           * Пример его изменения можно посмотреть в компоненте DataEntityCatalogList
           * Метод getActiveSort получает активную сортировку
           * Получаем данные сортировки 1 раз - при загрузке страницы, потому что у него всегда один вид
           */
          if (this.getSortListRequest()) {
            this.externalAPI.sortList = this.getSortListRequest()(data)
            this.externalAPI.activeSort = this.getActiveSort(this.externalAPI.sortList)
          }
          /**
           * getPaginationDataRequest по умолчанию вовзращает данные из пропса getPaginationData
           * Этот метод создан также, чтобы его можно легко изменить в других умных компонентах.
           * Пример его изменения можно посмотреть в компоненте DataEntityCatalogList
           */
          if (this.getPaginationDataRequest()(data)) {
            const { current, count, itemQuantity } = this.getPaginationDataRequest()(data)
            this.externalAPI.pageCurrent = current
            this.externalAPI.pageCount = count
            this.externalAPI.itemsCount = itemQuantity
          }
          this.externalAPI.initialLoad = false
        },
        onError: (error) => {
          this.externalAPI.response = error?.response?.data?.error?.description || 'Ошибка, попробуйте позже'
        },
      }
    },
    /**
     * Ручная смена страницы
     * @param {number} page - номер новой страницы
     */
    async changePage(page) {
      if (page !== this.externalAPI.pageCurrent && !this.externalAPI.loading) {
        this.localParams.page = page
        this.externalAPI.pageCurrent = page
        await this.request(this.getRequestParams())
        this.$emit('page-change', { ...this.externalAPI, ...this._api })
      }
    },
    /**
     * Подгрузка доп. страницы к существующему списку
     */
    async loadMore() {
      if (this.externalAPI.pageCurrent < this.externalAPI.pageCount && !this.externalAPI.loading) {
        const num = this.externalAPI.pageCurrent + 1
        this.externalAPI.pageCurrent = num
        this.localParams.page = num
        await this.request(this.getRequestParams({ strategy: 'append' }))
        this.$emit('load-more', { ...this.externalAPI, ...this._api })
      }
    },
    async sort({ name, value } = {}) {
      this.externalAPI.activeSort = {
        name,
        value,
      }
      this.localParams.sort = value
      await this.request(this.getRequestParams())
      this.$emit('sort', { ...this.externalAPI, ...this._api })
    },
    getLocalParams() {
      return this.localParams
    },
    getRequest() {
      return this.get
    },
    getPaginationDataRequest() {
      return this.getPaginationData
    },
    getListRequest() {
      return this.getList
    },
    getDataRequest(data) {
      return data
    },
    getSortListRequest() {
      return this.getSortList
    },
    getActiveSort(list = []) {
      return list.find(el => el.active)
    },
    replaceCurrentLoading() {
      return false
    },
  },
}
